import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlayCircle, Loader2, Video, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const GW = [["stripe", "Stripe (international cards)"], ["paystack", "Paystack (bank, Nigeria)"], ["flutterwave", "Flutterwave (bank & mobile money)"]];
const LANGS = [["en", "English"], ["fr", "Français"], ["sw", "Kiswahili"]];

export function PayoutGuideCard() {
  const [gateway, setGateway] = useState("stripe");
  const [language, setLanguage] = useState("en");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/guides/payout/mine").then(({ data }) => setMine(data.guides)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!mine.some((g) => g.status === "generating")) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [mine]);

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/guides/payout/request", { gateway, language });
      toast.success(data.status === "ready" ? "Guide attached to your account — play it below" : "Preparing your guide video (about 2 minutes). We'll email you when it's ready.");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail) || "Could not request guide"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="payout-guide-card">
      <div className="flex items-center gap-2"><Video className="h-4 w-4 text-[#007AFF]" /><h2 className="font-black text-xl tracking-tight">Need a walkthrough? Get the guide video</h2></div>
      <p className="mt-2 text-sm text-neutral-400">A short narrated video that walks you through connecting your payouts step by step and making your first test sale. It stays attached to your account.</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={gateway} onChange={(e) => setGateway(e.target.value)} data-testid="guide-gateway" className="h-10 rounded-full bg-[#0A0A0A] border border-[#27272A] px-3 text-sm">
          {GW.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="guide-language" className="h-10 rounded-full bg-[#0A0A0A] border border-[#27272A] px-3 text-sm">
          {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button onClick={request} disabled={busy} data-testid="guide-request-btn" className="rounded-full bg-white text-black hover:bg-neutral-200 font-semibold">
          {busy ? "Requesting…" : "Send me the guide video"}
        </Button>
      </div>
      {mine.length > 0 && (
        <ul className="mt-5 space-y-3" data-testid="guide-list">
          {mine.map((g) => (
            <li key={`${g.gateway}-${g.language}`} className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-3" data-testid={`guide-item-${g.gateway}-${g.language}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold">{GW.find(([k]) => k === g.gateway)?.[1]} · {LANGS.find(([k]) => k === g.language)?.[1]}</div>
                <div className="text-xs text-neutral-500 flex items-center gap-1.5" data-testid={`guide-status-${g.gateway}-${g.language}`}>
                  {g.status === "generating" && <><Loader2 className="h-3 w-3 animate-spin" /> Preparing…</>}
                  {g.status === "ready" && <><CheckCircle2 className="h-3 w-3 text-[#34C759]" /> Ready · {g.duration}s</>}
                  {g.status === "failed" && <span className="text-[#FF3B30]">Failed — request again</span>}
                </div>
              </div>
              {g.url && (
                <div className="mt-3 flex flex-wrap items-start gap-3">
                  <video src={`${API}${g.url}`} controls preload="metadata" className="h-64 w-[144px] rounded-md bg-black" data-testid={`guide-video-${g.gateway}-${g.language}`} />
                  <a href={`${API}${g.url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#007AFF] hover:underline"><PlayCircle className="h-3.5 w-3.5" /> Open full screen</a>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
